/**
 * Browser helpers for search boxes and page-scoped styles.
 *
 * `sanitizeInput` cleans the FAQ and glossary search terms (`components/FAQ/FAQ.tsx`,
 * `components/Glossary/Page/Page.tsx`, both with `maxLength` 50). `appendStyle` / `removeStyle`
 * add and remove a page's own `<style>` element (`components/Policies/PrivacyBody.tsx`).
 */

/**
 * Removes the characters `& / \ # , + ( ) $ ~ % . ^ ' " : * ? < > { }` from `input`, then cuts it
 * to `maxLength` characters.
 *
 * The result is used as a regular-expression source by `components/Shared/model/Contentful.tsx`
 * → `renderRichText` to highlight matches. `[`, `]` and `|` are kept, so the result isn't always a
 * valid or literal pattern.
 */
export const sanitizeInput = (input: string, maxLength: number) => {
	return cropText(input.replaceAll(/[&/\\#,+()$~%.^'":*?<>{}]/g, ""), maxLength);
};

/** Cuts `text` to at most `length` characters. */
const cropText = (text: string, length: number) => {
	return text.length > length ? text.substring(0, length) : text;
};

/**
 * Adds a `<style id={id}>` with `css` to `document.head`, once: a second call with the same `id`
 * does nothing. Browser only.
 */
export const appendStyle = (css: string, id: string): void => {
	if (!document?.head) return; // Prevent error if document.head doesn't exist

	if (document.getElementById(id)) return; // Prevent duplicate styles

	const style = document.createElement("style");
	style.id = id; // Assign an ID for easy removal
	style.innerHTML = css;
	document.head.appendChild(style);
};

/** Removes the `<style>` element `appendStyle` added under `id`, if it is there. */
export const removeStyle = (id: string): void => {
	if (!document?.head) return; // Prevent error if document.head doesn't exist

	const style = document.getElementById(id);
	if (style) {
		document.head.removeChild(style);
	}
};
